import {
  Component,
  EventEmitter,
  Output,
} from '@angular/core';
import { AiChatbotInputMessageComponent } from './ai-chatbot-input-message/ai-chatbot-input-message.component';
import { CommonModule } from '@angular/common';
import { AiApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/ai/ai-api';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedModule } from '../../../ui-components/shared.module';
import { AiChatbotMessageComponent } from './ai-chatbot-message/ai-chatbot-message.component';
import { AiChatbotWillyComponent } from './ai-chatbot-willy/ai-chatbot-willy.component';
import { AiChatbotHeaderComponent } from './ai-chatbot-header/ai-chatbot-header.component';
import { firstValueFrom } from 'rxjs';
import { StorageService } from '../../../@services/front/storage.service';

@Component({
    selector: 'vw-ai-chatbot',
    imports: [
        AiChatbotInputMessageComponent,
        CommonModule,
        TranslateModule,
        SharedModule,
        AiChatbotMessageComponent,
        AiChatbotWillyComponent,
        AiChatbotHeaderComponent,
    ],
    providers: [
        {
            provide: AiApi,
            useClass: AiApi,
        },
        {
            provide: AuthApi,
            useClass: AuthApi,
        },
    ],
    templateUrl: './ai-chatbot.component.html',
    styleUrl: './ai-chatbot.component.scss'
})
export class AiChatbotComponent {
  // Emiting input focus so we can display/hide the menu ai actions to let space for the mobile keyboard
  @Output() inputFocus = new EventEmitter<void>();
  @Output() inputBlur = new EventEmitter<void>();

  inputIsFocus = false;
  chatSessionId: string | undefined;
  currentMessage: string = '';
  willyLoading = false;
  willyAction: 'hi' | 'thinking' | 'explaining' | 'dontknow' = 'hi';
  isGuest = false;

  constructor(
    private aiApi: AiApi,
    private authApi: AuthApi,
    private translate: TranslateService,
  ) { }

  async ngOnInit() {
    this.currentMessage = await firstValueFrom(this.translate.get('willy.hi'));

    // Check if user is authenticated without fetching user data
    const token = this.authApi.decodeToken();
    this.isGuest = token['cognito:groups'].includes('GUEST');
  }

  // Send the message to the AI and get the response
  async sendMessageToWilly(message: string) {
    if (!this.willyLoading) {
      this.willyLoading = true;
      this.willyAction = 'thinking';
      this.currentMessage = await firstValueFrom(this.translate.get('willy.thinking'));

      try {
        const willyResponse = await this.aiApi.chatWithWilly({
          text: message,
          sessionId: this.chatSessionId,
        });
        this.chatSessionId = willyResponse.sessionId;
        this.currentMessage = willyResponse.text;
        this.willyAction = 'explaining';
      } catch (error: any) {
        let errorKey = 'willy.error.500';
        switch (error.data?.statusCode) {
          case 404:
            errorKey = 'willy.error.400';
            break;
          case 403:
            errorKey = 'willy.error.403';
            break;
        }
        this.willyAction = 'dontknow';
        this.currentMessage = await firstValueFrom(this.translate.get(errorKey));
      } finally {
        this.willyLoading = false;
      }
    }
  }

  // Emiting input focus so we can display/hide the menu ai actions to let space for the mobile keyboard
  onInputFocus() {
    this.inputIsFocus = true;
    this.inputFocus.emit();
  }

  // Emiting input blur so we can display/hide the menu ai actions to let space for the mobile keyboard
  onInputBlur() {
    this.inputIsFocus = false;
    this.inputBlur.emit();
  }

  suggestions: string[] = [
    'When should I water my plants?',
    'How much water should I give my plants?',
    'How often should I water my plants?',
    'When should I fertilize my plants?',
  ];
}
